import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BsBoxArrowRight } from 'react-icons/bs';
import { Outlet, useLocation, useNavigate } from 'react-router';

import {
  Button,
  LanguageSwitcher,
  ResponsiveModalBottomSheet,
  ScrollToTop,
} from '@/components';
import BackButton from '@/pages/components/BackButton';
import { ProfileService } from '@/services/profileService';

const ProfileLayout: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const didTryHydrateRef = useRef(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);

  useEffect(() => {
    if (didTryHydrateRef.current) return;
    didTryHydrateRef.current = true;

    if (!ProfileService.isLoggedIn()) return;
    if (ProfileService.getStoredProfile()) return;

    (async () => {
      try {
        const user = await ProfileService.getMe();
        ProfileService.userToProfile(user);
      } catch (error) {
        console.error('Failed to load profile after login:', error);
      }
    })();
  }, []);

  const handleBack = () => {
    void navigate(-1);
  };

  const handleConfirmLogout = () => {
    setShowLogoutConfirm(false);
    ProfileService.logout();
    void navigate('/profile/phone', { replace: true });
  };

  const canGoBack = location.key !== 'default';
  const isProfileHome = location.pathname === '/profile/home';
  const isPhoneEntryPage = location.pathname === '/profile/phone';
  const shouldShowBackButton = canGoBack && !isPhoneEntryPage;

  return (
    <div className="flex min-h-screen flex-col">
      <ScrollToTop />

      <header className="sticky top-0 z-10 bg-white/90 backdrop-blur">
        <div className="container mx-auto flex items-center justify-between px-4 py-4">
          {isProfileHome ? (
            <button
              type="button"
              onClick={() => setShowLogoutConfirm(true)}
              className="text-primary-text inline-flex items-center gap-1.5 rounded px-1 py-1 text-lg leading-9 font-normal transition hover:bg-black/5 focus:outline-none"
              aria-label={t('profile.home.actions.logout')}
            >
              <BsBoxArrowRight className="size-6" aria-hidden="true" />
              <span>{t('profile.home.actions.logout')}</span>
            </button>
          ) : shouldShowBackButton ? (
            <BackButton onClick={handleBack} />
          ) : (
            <div />
          )}
          <LanguageSwitcher />
        </div>
      </header>

      <main className="container mx-auto flex w-full flex-1 flex-col px-4 py-8">
        <Outlet />
      </main>

      <ResponsiveModalBottomSheet
        isOpen={showLogoutConfirm}
        onClose={() => setShowLogoutConfirm(false)}
        showCloseOnMobile={false}
        closable={true}
      >
        <div className="flex flex-col gap-4 px-4 pb-6">
          <div className="flex flex-col gap-2 text-center">
            <p className="text-xl leading-7 font-semibold text-black">
              {t('profile.home.logoutConfirm.title')}
            </p>
            <p className="text-secondary-text leading-6">
              {t('profile.home.logoutConfirm.message')}
            </p>
          </div>
          <div className="flex gap-4">
            <Button
              variant="secondary"
              onClick={() => setShowLogoutConfirm(false)}
              className="flex-1"
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={handleConfirmLogout}
              className="flex-1"
            >
              {t('profile.home.logoutConfirm.confirm')}
            </Button>
          </div>
        </div>
      </ResponsiveModalBottomSheet>
    </div>
  );
};

export default ProfileLayout;
